/**
 * WHAT THE RUN-HISTORY QUALITY COLUMN SAYS, AND WHY IT IS THAT COLOUR.
 *
 * THE DEFECT THIS REPLACES. Both run tables rendered `quality_result` — the enum string
 * `pass` / `degraded` / `fail` — coloured green / amber / red, and consulted no threshold at
 * all. `quality_result` comes from `ocr_worker._quality_check`: it answers "did this arrive
 * readable, with the expected page count". The quality SLA answers "did this score at least
 * the minimum the job configures". They are DIFFERENT AXES, and the column was labelled for
 * the second while showing the first, so a fax scoring 12 against a minimum of 70 could read
 * a green `pass` — the engine had the right answer and the surface showed another one.
 *
 * THE VERDICT IS READ, NEVER RECOMPUTED. Nothing here compares `quality_score` against
 * `min_quality_score_effective`. The stored verdict (`quality_sla_breached`, and
 * `quality_verdict` derived from it) records the threshold in force AT MEASUREMENT TIME, while
 * the effective minimum is whatever the job says NOW — a job's minimum is mutable and a
 * verdict already reached is not, so recomputing would silently re-judge historical rows
 * against a threshold they were never measured against. `min_quality_score_effective` is read
 * for ONE thing only: whether the axis is under an SLA at all, which is a question about
 * CONFIGURATION and not a comparison of the score to anything.
 *
 * TONE IS A ROLE, NOT A COLOUR. This module returns `good` / `bad` / `neutral`; the component
 * maps those to the palette. That keeps the rule testable as a rule — a test that pinned a hex
 * teaches the next reader to update the hex rather than to check the rule.
 *
 * NEUTRAL IS NEVER GREEN, AND THAT IS THE POINT. An unmeasured fax and an unjudged batch
 * aggregate both reach a reader as neutral with a label saying so. Green on something nobody
 * judged is the flattering direction, and an instrument whose readings are treated as evidence
 * must not err in it.
 */

/** The fields of a history row this rule reads. A subset of `Run`, so either can be passed. */
export interface QualityRow {
  kind?: 'run' | 'batch';
  quality_score?: number | null;
  /** The STORED verdict: true = under the minimum, false = met it, null = no verdict. */
  quality_sla_breached?: boolean | null;
  /** pass | fail | undecided | not_configured — derived by the backend from the column above. */
  quality_verdict?: string | null;
  /** The job's minimum NOW. Read only to tell "no SLA on this axis" from "not measured". */
  min_quality_score_effective?: number | null;
  /** For a batch: how many items the aggregate verdict was reached over. */
  quality_sla_denominator?: number | null;
  quality_sla_reason?: string | null;
}

/**
 * The five answers this column can give. Each is a DIFFERENT FACT — collapsing any two is the
 * class of defect this module exists to stop.
 *
 *  - `pass`            the stored verdict says the score met the minimum in force when measured.
 *  - `fail`            the stored verdict says it did not.
 *  - `not_configured`  the job puts no minimum on quality, so nothing was asked of this axis.
 *                      Green by the operator's explicit rule: there is no failure to report.
 *  - `not_measured`    no score exists. Never a pass — a blank reading is not a good one.
 *  - `not_judged`      a score (or a batch's aggregate mean) exists, but no verdict was reached
 *                      against a minimum. Distinct from `not_measured` because the measurement
 *                      DID happen, and distinct from `not_configured` because a minimum may
 *                      well be configured now — it just was not what this row was judged under.
 */
export type QualityCellKind =
  | 'pass'
  | 'fail'
  | 'not_configured'
  | 'not_measured'
  | 'not_judged';

/** The colour ROLE. `neutral` covers both "no answer" states and is deliberately not green. */
export type QualityCellTone = 'good' | 'bad' | 'neutral';

export interface QualityCellState {
  kind: QualityCellKind;
  tone: QualityCellTone;
  /** The score to display, when there is one. Null renders as the label alone. */
  score: number | null;
  /** Short text for the cell. Carries the state when no score exists. */
  label: string;
  /** The full explanation, for the cell's title attribute. */
  title: string;
  /** True when the row is an aggregate over items rather than one measurement. */
  aggregate: boolean;
}

/** One decimal place, and 0 survives — a score of 0 is a real, catastrophic reading. */
function fmtScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function batchDenominatorPhrase(row: QualityRow): string {
  const n = row.quality_sla_denominator;
  if (typeof n !== 'number') return 'Open the report for the population it was judged over.';
  return `Judged over ${n} item(s) whose job configures a minimum score; open the report for met/judged and the exclusions.`;
}

/**
 * A BATCH ROW IS ITS OWN BRANCH, because every input means something else on it.
 *
 * `quality_score` is an aggregate MEAN over items, not a measurement of one fax.
 * `min_quality_score_effective` is NULL by construction — a per-fax threshold does not apply
 * to an aggregate — so the run rule's "no minimum configured is a pass" reading would paint
 * EVERY batch row green, which is a pass asserted over an aggregate nobody judged. The batch's
 * actual answer is its stored verdict, projected onto `quality_sla_breached` from the
 * statistics snapshot, and a batch with no quality vote is NOT-JUDGED rather than green.
 */
function batchState(row: QualityRow): QualityCellState {
  const score = typeof row.quality_score === 'number' ? row.quality_score : null;
  const breached = row.quality_sla_breached;
  const scoreText = score === null ? '' : `${fmtScore(score)} mean`;

  if (breached === true) {
    return {
      kind: 'fail',
      tone: 'bad',
      score,
      label: score === null ? 'quality FAIL' : `${scoreText} ⚠️`,
      title: `Quality FAILED for this batch: too few items met their minimum score. ${batchDenominatorPhrase(row)}`,
      aggregate: true,
    };
  }
  if (breached === false) {
    return {
      kind: 'pass',
      tone: 'good',
      score,
      label: score === null ? 'quality pass' : `${scoreText} ✓`,
      title: `Quality PASSED for this batch: enough items met their minimum score. ${batchDenominatorPhrase(row)}`,
      aggregate: true,
    };
  }
  return {
    kind: score === null ? 'not_measured' : 'not_judged',
    tone: 'neutral',
    score,
    label: score === null ? 'not measured' : `${scoreText} · not SLA-judged`,
    title:
      score === null
        ? 'No quality score for this batch yet — its statistics have not been computed, or no item was scored. Not a pass.'
        : 'This is the MEAN score over the batch\u2019s items and no quality SLA verdict was reached: either no item\u2019s job configures a minimum, or the statistics predate the verdict. Not a pass.',
    aggregate: true,
  };
}

/**
 * The state of the quality cell for one history row.
 *
 * ORDER IS LOAD-BEARING. A stored FAIL is decided before anything else, so a missing score can
 * never mask a breach the backend did report; then a missing score is decided before a pass,
 * so an absent reading can never be promoted to one.
 */
export function qualityCellState(row: QualityRow): QualityCellState {
  if (row.kind === 'batch') return batchState(row);

  const score = typeof row.quality_score === 'number' ? row.quality_score : null;
  const breached = row.quality_sla_breached;
  const verdict = row.quality_verdict;
  const minimum = row.min_quality_score_effective;
  // Whether the axis is under an SLA AT ALL. A CONFIGURATION question — `0` is a real minimum
  // ("a score must exist, any value is acceptable"), so the TYPE is tested rather than
  // truthiness, exactly as `utils/sla.ts` does for a 0-minute leg threshold.
  const configuredNow = typeof minimum === 'number';
  const scoreText = score === null ? '' : fmtScore(score);

  // A BREACH, from the stored column or from the verdict the backend derived from it.
  if (breached === true || verdict === 'fail') {
    return {
      kind: 'fail',
      tone: 'bad',
      score,
      label: score === null ? 'FAIL' : `${scoreText} ⚠️`,
      title: configuredNow
        ? `Quality FAILED: scored under the minimum in force when it was measured (the job\u2019s minimum is now ${minimum}).`
        : 'Quality FAILED: scored under the minimum in force when it was measured. The job configures none now, which does not undo a verdict already reached.',
      aggregate: false,
    };
  }

  // NO SCORE. NOT MEASURED, and NEVER GREEN — not even when the job puts no minimum on
  // quality. `not_configured` is green because there is no failure to report, but that rule
  // applies to a MEASUREMENT nobody set a bar for; with no score there is nothing on the row
  // for the green to be about, and a blank cell tinted green reads as a pass. The two reasons
  // are still named apart in the title: a configured minimum means an answer is owed, no
  // minimum means none is coming.
  if (score === null) {
    return {
      kind: 'not_measured',
      tone: 'neutral',
      score: null,
      label: 'not measured',
      title: configuredNow
        ? `Not measured: this job requires at least ${minimum} but no score was recorded for this fax${row.quality_sla_reason ? ` (${row.quality_sla_reason})` : ''}. Not a pass.`
        : 'Not measured: no score was recorded for this fax, and this job puts no minimum on quality — so no verdict is coming. Not a pass.',
      aggregate: false,
    };
  }

  if (breached === false || verdict === 'pass') {
    return {
      kind: 'pass',
      tone: 'good',
      score,
      label: `${scoreText} ✓`,
      title: configuredNow
        ? `Quality PASSED: met the minimum in force when it was measured (the job\u2019s minimum is now ${minimum}).`
        : 'Quality PASSED against the minimum in force when it was measured. The job configures none now.',
      aggregate: false,
    };
  }

  // A SCORE, AND NO STORED VERDICT. Two cases, told apart by whether a minimum exists now.
  if (!configuredNow && verdict === 'not_configured') {
    return {
      kind: 'not_configured',
      tone: 'good',
      score,
      label: scoreText,
      title:
        'Scored, and this job puts no minimum on quality — nothing was asked of this axis, so there is no failure to report. Set a minimum on the job to make this score a verdict.',
      aggregate: false,
    };
  }
  return {
    kind: 'not_judged',
    tone: 'neutral',
    score,
    label: `${scoreText} · not SLA-judged`,
    title: configuredNow
      ? `Scored, but no quality SLA verdict was stored for this fax: it was not measured against a minimum, even though the job requires ${minimum} now. A minimum set after a measurement does not re-judge it. Not a pass.`
      : 'Scored, but no quality SLA verdict was stored for this fax. Not a pass.',
    aggregate: false,
  };
}
